/**
 * BizBirdCard — kid-friendly profile card for the four DISC birds.
 * Used on the elementary landing page and inside Mission 2.
 *
 * Prefers an illustrated `image` when provided; falls back to `emoji`
 * so older callers (or stubs) still render something.
 */
export default function BizBirdCard({
  emoji,
  image,
  name,
  role,
  description,
  selected = false,
  onSelect,
  testId,
}) {
  const interactive = typeof onSelect === "function";

  const base =
    "rounded-3xl p-5 text-left transition-all flex flex-col gap-3 ring-2";
  const stateClass = selected
    ? "bg-yellow-50 ring-yellow-500 shadow-lg"
    : interactive
    ? "bg-white ring-slate-200 hover:ring-yellow-400 hover:shadow-md cursor-pointer hover:-translate-y-0.5"
    : "bg-white ring-slate-200";

  const Tag = interactive ? "button" : "div";

  return (
    <Tag
      type={interactive ? "button" : undefined}
      onClick={interactive ? onSelect : undefined}
      className={`${base} ${stateClass}`}
      data-testid={testId}
    >
      {image ? (
        <div
          className={`w-24 h-24 sm:w-28 sm:h-28 rounded-2xl overflow-hidden ring-2 ${
            selected ? "ring-yellow-400" : "ring-yellow-100"
          } bg-gradient-to-br from-yellow-50 to-amber-50 flex items-center justify-center`}
        >
          <img
            src={image}
            alt={`${name} illustration`}
            className="w-full h-full object-contain p-1"
            loading="lazy"
            decoding="async"
          />
        </div>
      ) : (
        <div className="text-5xl" aria-hidden="true">
          {emoji}
        </div>
      )}
      <div className="flex items-baseline gap-2">
        <h4 className="text-xl font-bold text-gray-900">{name}</h4>
        <span className="text-xs font-semibold text-yellow-700 uppercase tracking-wide">
          {role}
        </span>
      </div>
      <p className="text-sm text-gray-600 leading-relaxed">{description}</p>
      {selected && (
        <span className="self-start bg-yellow-500 text-white text-xs font-bold px-2.5 py-1 rounded-full">
          Your Pick
        </span>
      )}
    </Tag>
  );
}
